import React from 'react';
import PropTypes from 'prop-types';

import { Button } from 'components/Button';
import { Heading, HeadingBoundary } from 'components/Heading';
import { Grid } from 'components/Grid';
import { Image } from 'components/Image';
import { Text } from 'components/Text';
import { VideoAutoplay } from 'components/VideoAutoplay';
import { VideoEmbed } from 'components/VideoEmbed';
import { cx, capitalizeFirstLetter } from 'utility';
import * as styles from './ContentMedia.module.scss';

const propTypes = {
    alignment: PropTypes.oneOf(['ltr', 'rtl']),
    buttonColor: PropTypes.oneOf(['primary', 'secondary']),
    buttonStyle: PropTypes.oneOf(['ghost', 'outline', 'contained']),
    buttonText: PropTypes.string,
    buttonUrl: PropTypes.object,
    className: PropTypes.string,
    content: PropTypes.string,
    gridClassName: PropTypes.string,
    image: PropTypes.PropTypes.shape({
        alt: PropTypes.string,
        dimensions: PropTypes.shape({
            height: PropTypes.number,
            width: PropTypes.number,
        }),
        url: PropTypes.string,
    }),
    title: PropTypes.string,
    titleFontSize: PropTypes.number,
    videoAutoplay: PropTypes.bool,
    videoEmbedUrl: PropTypes.string,
    videoEmbedTitle: PropTypes.string,
    videoUpload: PropTypes.string,
};

const defaultProps = {
    alignment: '',
    buttonColor: 'secondary',
    buttonStyle: 'contained',
    buttonText: '',
    buttonUrl: {},
    className: '',
    content: '',
    gridClassName: '',
    image: {},
    title: '',
    titleFontSize: 7,
    videoAutoplay: true,
    videoEmbedUrl: '',
    videoEmbedTitle: '',
    videoUpload: '',
};

function getClasses({ className, alignment }) {
    const classes = cx(
        className,
        styles['contentMedia'],
        styles[`contentMedia${capitalizeFirstLetter(alignment)}`],
    );

    return classes || undefined;
}

function ContentMedia({
    alignment,
    buttonColor,
    buttonStyle,
    buttonText,
    buttonUrl,
    className,
    content,
    gridClassName,
    image,
    title,
    titleFontSize,
    videoAutoplay,
    videoEmbedUrl,
    videoEmbedTitle,
    videoUploadUrl,
}) {
    const classes = getClasses({ className, alignment });

    return (
        <div className={classes}>
            <Grid className={gridClassName}>
                <div className={styles['container']}>
                    <HeadingBoundary>
                        <div className={styles['mediaContainer']}>
                            <div className={styles['mediaWrapper']}>
                                {image?.url && (
                                    <Image
                                        className={styles['image']}
                                        image={image}
                                        size="small"
                                    />
                                )}
                                {(videoUploadUrl && !videoEmbedUrl) && (
                                    <VideoAutoplay
                                        autoPlay={videoAutoplay}
                                        className={styles['videoUpload']}
                                        videoUploadUrl={videoUploadUrl}
                                    />
                                )}
                                {(videoEmbedUrl && !videoUploadUrl) && (
                                    <VideoEmbed
                                        className={styles['videoEmbed']}
                                        title={videoEmbedTitle}
                                        videoEmbedUrl={videoEmbedUrl}
                                    />
                                )}
                            </div>
                        </div>
                        <div className={styles['contentContainer']}>
                            {title && (
                                <Heading className={styles['title']} size={titleFontSize}>{title}</Heading>
                            )}
                            {content && (
                                <Text as="p" className={styles['content']} size={3}>{content}</Text>
                            )}
                            {(buttonText && (buttonUrl?.url || buttonUrl?.uid)) && (
                                <Button className={styles['button']} color={buttonColor} style={buttonStyle} to={buttonUrl}>{buttonText}</Button>
                            )}
                        </div>
                    </HeadingBoundary>
                </div>
            </Grid>
        </div>
    );
}

ContentMedia.propTypes = propTypes;
ContentMedia.defaultProps = defaultProps;

export { ContentMedia };
